import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { getWatchlist, getWatchlistTickers, removeFromWatchlist, saveToWatchlist, setWatchlistAlert, setAllWatchlistAlerts } from "@/lib/storage";
import { compare, thesisVisualData } from "@/lib/api";
import { computeCustomRatios, autoInputsFromData } from "@/lib/customRatios";
import { sourceUnavailable } from "@/lib/dataSource";
import { fmtPrice, fmtNum, fmtPctSigned, ratioColor } from "@/lib/format";
import { useThresholds } from "@/lib/useThresholds";
import { useAuth } from "@/lib/auth";
import { useFx, useDonutFx } from "@/lib/fx";
import { useI18n } from "@/lib/i18n";
import { notifyGet, notifyPut } from "@/lib/api";
import AlertToggle from "@/components/AlertToggle";
import { alertInfoText } from "@/components/AlertInfoBanner";
import { LevelFilters, useLevelFilters, passesLevelFilters, levelFiltersActive } from "@/components/LevelFilters";
import MasterAlertToggle from "@/components/MasterAlertToggle";
import { ViewToggle } from "@/components/ViewToggle";
import { CompanyCard } from "@/components/CompanyCard";
import { NextEarnings, nextEarningsInfo } from "@/components/NextEarnings";
import { CardSort } from "@/components/CardSort";
import { SortableTh, makeSorter, nextSort } from "@/components/SortableTh";
import { Trash2, ArrowRight, Plus, PieChart } from "lucide-react";
import { toast } from "sonner";
import TickerAutocomplete from "@/components/TickerAutocomplete";
import ConfirmDialog from "@/components/ConfirmDialog";
import { PortfolioDonut } from "@/components/PortfolioDonut";
import { QUAL_TIPS } from "@/lib/qualTips";

const WL_NUMERIC_KEYS = new Set(["price", "mcap", "rc", "rv", "score", "tam", "kpi"]);
const WL_SORT_OPTIONS = [
    { key: "ticker", label: "Alfabético (Ticker)" },
    { key: "name", label: "Alfabético (Nombre)" },
    { key: "price", label: "Precio" },
    { key: "mcap", label: "Capitalización" },
    { key: "rc", label: "Ratio Compra" },
    { key: "rv", label: "Ratio Venta" },
    { key: "score", label: "Score" },
    { key: "tam", label: "TAM" },
    { key: "kpi", label: "Coef KPI" },
    { key: "earnings", label: "Próx. resultados" },
];
const WL_ACCESSORS = {
    ticker: (row) => row.data?.ticker,
    name: (row) => row.data?.name,
    price: (row) => row.data?.current_price,
    mcap: (row) => row.data?.market_cap,
    rc: (row) => row.data?.custom_ratios?.ratio_compra_pct,
    rv: (row) => row.data?.custom_ratios?.ratio_venta_pct,
    sector: (row) => row.data?.sector || "Sin clasificar",
};

/**
 * For each watchlist entry, fetch fresh Yahoo data and apply the user's saved
 * overrides (if any) before recomputing the custom ratios on the client side.
 * Price is always taken from Yahoo (current_price never overridden).
 */
const applyEntryOverrides = (companyData, entry) => {
    if (!companyData || companyData.error) return companyData;
    // «Nuestro» without official figures for the company: no ratio, like the ficha.
    if (sourceUnavailable(companyData)) return { ...companyData, custom_ratios: {} };
    if (!entry || !entry.overrides || Object.keys(entry.overrides).length === 0) return companyData;

    const auto = autoInputsFromData(companyData);
    const merged = { ...auto };
    for (const [k, v] of Object.entries(entry.overrides)) {
        if (k === "current_price") continue; // never override price
        merged[k] = v;
    }
    const cr = computeCustomRatios(merged);
    return { ...companyData, custom_ratios: cr };
};

export default function Watchlist() {
    const [entries, setEntries] = useState([]);
    const [rows, setRows] = useState([]);
    const rowsRef = useRef([]);
    useEffect(() => { rowsRef.current = rows; }, [rows]);
    const [qual, setQual] = useState({});  // ticker → { score, tam, kpi_coef }
    const [loading, setLoading] = useState(false);
    const [sort, setSort] = useState(null);
    const [view, setView] = useState(() => localStorage.getItem("vs:watchlist-view") || "table");
    const changeView = (v) => { setView(v); localStorage.setItem("vs:watchlist-view", v); };
    const { user } = useAuth();
    const [notify, setNotify] = useState(null);
    const { display: displayCur, convert: fxConvert } = useFx();
    const { donutCur, toDonut } = useDonutFx();
    const { t } = useI18n();
    useThresholds(); // re-render on threshold changes
    const donutRef = useRef(null);
    const scrollToDonuts = () => donutRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    // Qualitative layer (score / TAM / coef KPI) from the user's theses, by ticker.
    useEffect(() => {
        thesisVisualData()
            .then((d) => {
                const m = {};
                (d.rows || []).forEach((r) => { m[r.ticker] = { score: r.avg_overall_score, tam: r.sum_tam_score, kpi_coef: r.kpi_coef }; });
                setQual(m);
            })
            .catch(() => setQual({}));
    }, []);

    useEffect(() => {
        if (!user) { setNotify(null); return; }
        notifyGet().then((n) => {
            // The per-row bell is now the single source of truth for activation.
            // Auto-enable the global flags silently so emails actually flow when
            // the user toggles a row bell. We only push to backend if something
            // is missing to avoid spurious writes on every mount.
            const desired = {
                enabled: true,
                cross_buy_zone: n?.cross_buy_zone !== false,
                cross_sell_zone: n?.cross_sell_zone !== false,
            };
            setNotify(desired);
            if (!n?.enabled || n?.cross_buy_zone === false || n?.cross_sell_zone === false) {
                notifyPut(desired).catch(() => { /* non-fatal */ });
            }
        }).catch(() => setNotify({ enabled: true, cross_buy_zone: true, cross_sell_zone: true }));
    }, [user]);

    // Kept for potential future preferences UI; not used by the simplified banner.
    // eslint-disable-next-line no-unused-vars
    const updateNotify = async (patch) => {
        const next = { ...(notify || {}), ...patch };
        setNotify(next);
        try { await notifyPut(next); toast.success("Preferencias guardadas"); }
        catch { toast.error("No se pudieron guardar las preferencias"); }
    };

    const load = async (es) => {
        if (!es.length) { setRows([]); return; }
        setLoading(true);
        try {
            const tickers = es.map(e => e.ticker);
            const r = await compare(tickers);
            const byTicker = Object.fromEntries((r.results || []).map(x => [x.ticker, x]));
            const result = es.map(entry => {
                const cd = byTicker[entry.ticker] || { ticker: entry.ticker, error: "Sin datos" };
                return { entry, data: applyEntryOverrides(cd, entry) };
            });
            setRows(result);
        } catch (e) { toast.error("Error cargando watchlist"); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const es = getWatchlist();
        setEntries(es);
        if (es.length > 100) {
            toast.warning(`Tu watchlist tiene ${es.length} tickers (límite recomendado: 100). El rendimiento puede verse afectado.`);
        }
        load(es);
        // Stay in sync with cache updates (per-row alert toggle, adds/removes from another
        // page, or changes pulled from the cloud on another device). When the SET of tickers
        // changes (add/remove) we refetch the rows so the new list renders with data; when only
        // an entry's fields change (e.g. alert) we just re-merge without a Yahoo refetch.
        const onChange = () => {
            const next = getWatchlist();
            setEntries(next);
            const nextKey = next.map(e => e.ticker).sort().join(",");
            const curKey = rowsRef.current.map(r => r.entry.ticker).sort().join(",");
            if (nextKey !== curKey) {
                load(next);
            } else {
                setRows((prev) => prev.map(r => {
                    const updated = next.find(e => e.ticker === r.entry.ticker);
                    return updated ? { ...r, entry: updated } : r;
                }));
            }
        };
        window.addEventListener("vs:watchlist-changed", onChange);
        return () => window.removeEventListener("vs:watchlist-changed", onChange);
    }, []);

    const [confirmDel, setConfirmDel] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [addInput, setAddInput] = useState("");
    const addTicker = (symbol) => {
        const tk = (symbol || "").trim().toUpperCase();
        if (!tk) return;
        if (getWatchlist().some(e => e.ticker === tk)) {
            toast.message(`${tk} ya está en Nivel 2`);
            setShowAdd(false); setAddInput("");
            return;
        }
        saveToWatchlist(tk);   // dispatches vs:watchlist-changed → list reloads with data
        toast.success(`${tk} añadida a Nivel 2`);
        setShowAdd(false); setAddInput("");
    };
    const handleRemove = (t) => setConfirmDel(t);
    const doRemove = () => {
        const t = confirmDel;
        if (!t) return;
        const list = removeFromWatchlist(t);
        setEntries(list);
        setRows(rows.filter(r => r.entry.ticker !== t));
        setConfirmDel(null);
        toast("Quitada de Nivel 2");
    };

    const activeAlertCount = entries.filter(e => e.alert_enabled).length;
    const toggleAllAlerts = (next) => {
        if (!user) { toast.message(t("alerts.requires_login")); return; }
        if (entries.length === 0) return;
        const list = setAllWatchlistAlerts(next);
        setEntries(list);
        toast.success(next ? "Alertas activadas en toda la watchlist" : "Alertas desactivadas en toda la watchlist");
    };

    const onSort = (key) => setSort((prev) => nextSort(prev, key, WL_NUMERIC_KEYS));
    const accessors = {
        ...WL_ACCESSORS,
        score: (row) => qual[row.data?.ticker]?.score,
        tam: (row) => qual[row.data?.ticker]?.tam,
        kpi: (row) => qual[row.data?.ticker]?.kpi_coef,
        earnings: (row) => { const info = nextEarningsInfo(row.data?.next_earnings_date); return info ? info.days : null; },
    };
    const [filters, setFilters] = useLevelFilters("vs.levelFilters.watchlist");
    const sectorOptions = useMemo(() => Array.from(new Set(rows.map((r) => r.data?.sector || "Sin clasificar")))
        .sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" })), [rows]);
    const sortedRows = useMemo(() => {
        const sorted = (!sort || !accessors[sort.key]) ? rows : [...rows].sort(makeSorter(accessors[sort.key], sort.dir));
        if (!levelFiltersActive(filters)) return sorted;
        return sorted.filter(({ entry, data: r }) => !r.error && passesLevelFilters({
            ticker: r.ticker, name: r.name, sector: r.sector, mode: entry.mode,
            rc: r.custom_ratios?.ratio_compra_pct, rv: r.custom_ratios?.ratio_venta_pct,
            score: qual[r.ticker]?.score, tam: qual[r.ticker]?.tam, kpi: qual[r.ticker]?.kpi_coef,
        }, filters));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, sort, qual, filters]);

    // Composition donuts (by company / by sector): Nivel 2 has no invested amounts,
    // so weight by market cap instead — normalized to a common currency (see
    // useDonutFx — never mixes currencies silently).
    const mcapMap = {};
    const sectorMap = {};
    const sectorCompaniesMap = {};
    for (const { data: r } of rows) {
        if (!r || r.error || r.market_cap == null) continue;
        const val = toDonut(r.market_cap, r.currency || "USD");
        if (val == null || val <= 0) continue;
        mcapMap[r.ticker] = val;
        const sector = r.sector || "Sin clasificar";
        sectorMap[sector] = (sectorMap[sector] || 0) + val;
        if (!sectorCompaniesMap[sector]) sectorCompaniesMap[sector] = [];
        sectorCompaniesMap[sector].push({ key: r.ticker, label: r.ticker, value: val });
    }
    const mcapHoldings = Object.entries(mcapMap).map(([ticker, value]) => ({ key: ticker, label: ticker, value }));
    const mcapSectorHoldings = Object.entries(sectorMap).map(([sector, value]) => ({
        key: sector, label: sector, value, companies: sectorCompaniesMap[sector],
    }));

    return (
        <div data-testid="watchlist-page">
            <div className="flex justify-between items-end mb-6 gap-3 flex-wrap">
                <div>
                    <div className="overline text-[#B32A22]">{t("watchlist.tag")}</div>
                    <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">{t("watchlist.title")}</h1>
                    {entries.length > 0 && (
                        <div className="text-xs text-[#4A4A4A] font-mono mt-1" data-testid="watchlist-count">
                            {t("watchlist.count_companies", { n: entries.length })}
                            {activeAlertCount > 0 && <span className="opacity-70"> · {t("watchlist.count_with_alert", { n: activeAlertCount })}</span>}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {entries.length > 0 && (
                        <button onClick={scrollToDonuts} className="btn-ghost inline-flex items-center gap-1 mr-3" data-testid="watchlist-goto-charts">
                            <PieChart size={14} /> Ir a gráfico
                        </button>
                    )}
                    <ViewToggle view={view} onChange={changeView} testid="watchlist-view-toggle" />
                    <button onClick={() => { setAddInput(""); setShowAdd(true); }} className="btn-primary inline-flex items-center gap-1" data-testid="watchlist-add-btn">
                        <Plus size={14} /> Añadir
                    </button>
                    <Link to="/compare" className="btn-ghost" data-testid="watchlist-to-compare">{t("nav.compare")} <ArrowRight size={12} className="inline ml-1" /></Link>
                </div>
            </div>

            {!user && (
                <div className="border border-[#052049] bg-white p-4 mb-6 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="login-prompt">
                    <div>
                        <div className="overline text-[#052049] mb-1">{t("watchlist.login_prompt_tag")}</div>
                        <div className="text-[#4A4A4A]">{t("watchlist.login_prompt_text")}</div>
                    </div>
                </div>
            )}

            {entries.length > 0 && (
                <LevelFilters filters={filters} setFilters={setFilters} sectorOptions={sectorOptions}
                              shown={sortedRows.length} total={rows.length} testid="watchlist-filters" />
            )}

            {!entries.length ? (
                <div className="border border-black bg-white p-12 text-center" data-testid="watchlist-empty">
                    <div className="font-serif text-3xl mb-2">{t("watchlist.empty_title")}</div>
                    <div className="text-sm text-[#4A4A4A] mb-6">{t("watchlist.empty_sub")}</div>
                    <Link to="/" className="btn-primary">{t("watchlist.empty_cta")}</Link>
                </div>
            ) : view === "cards" ? (
                <>
                <CardSort options={WL_SORT_OPTIONS} sort={sort} onChange={setSort} testid="watchlist-card-sort" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="watchlist-cards">
                    {loading && <div className="col-span-full text-center py-8 font-mono text-[#4A4A4A]">{t("common.loading")}</div>}
                    {!loading && sortedRows.map(({ entry, data: r }) => {
                        if (r.error) return (
                            <div key={r.ticker} className="border border-[#B32A22]/40 bg-white p-3 flex items-center justify-between" data-testid={`watchlist-card-${r.ticker}`}>
                                <span className="font-mono font-bold">{r.ticker}</span>
                                <span className="text-[#B32A22] text-xs">{r.error}</span>
                                <button onClick={() => handleRemove(r.ticker)} className="text-[#B32A22]" data-testid={`remove-${r.ticker}`}><Trash2 size={12} /></button>
                            </div>
                        );
                        const cr = r.custom_ratios || {};
                        const q = qual[r.ticker];
                        const useDisp = displayCur && displayCur !== "NATIVE";
                        return (
                            <CompanyCard
                                key={r.ticker}
                                testid={`watchlist-card-${r.ticker}`}
                                ticker={r.ticker}
                                name={r.name}
                                price={useDisp ? fxConvert(r.current_price, r.currency) : r.current_price}
                                priceCur={useDisp ? displayCur : r.currency}
                                mcap={useDisp ? fxConvert(r.market_cap, r.currency) : r.market_cap}
                                rc={cr.ratio_compra_pct}
                                rv={cr.ratio_venta_pct}
                                score={q?.score}
                                tam={q?.tam}
                                kpi={q?.kpi_coef}
                                nextEarnings={r.next_earnings_date}
                                badges={<span className={`overline px-1.5 py-0.5 border text-[9px] ${entry.mode === "manual" ? "border-[#1D7044] text-[#1D7044]" : "border-black/30 text-[#4A4A4A]"}`} data-testid={`mode-${r.ticker}`}>{entry.mode === "manual" ? "MAN" : "AUTO"}</span>}
                                actions={<>
                                    <AlertToggle enabled={!!entry.alert_enabled} onChange={(v) => { setWatchlistAlert(r.ticker, v); toast.success(v ? t("alerts.row_on") : t("alerts.row_off")); }} testid={r.ticker} />
                                    <button onClick={() => handleRemove(r.ticker)} className="text-[#B32A22] hover:text-black" data-testid={`remove-${r.ticker}`}><Trash2 size={13} /></button>
                                </>}
                            />
                        );
                    })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" ref={donutRef}>
                    <PortfolioDonut items={mcapHoldings} currency={donutCur} testid="watchlist-donut" title="Capitalización por empresa" columns={2} linkTickers totalCompact showValue />
                    <PortfolioDonut items={mcapSectorHoldings} currency={donutCur} testid="watchlist-donut-sector" title="Capitalización por sector" totalCompact showValue />
                </div>
                </>
            ) : (
                <>
                <div className="border border-black bg-white overflow-x-auto" data-testid="watchlist-table">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-black">
                                <SortableTh label={t("watchlist.col_ticker")} sortKey="ticker" sort={sort} onSort={onSort} align="left" className="sticky left-0 z-20 bg-white w-[76px] min-w-[76px]" />
                                <SortableTh label="Sector" sortKey="sector" sort={sort} onSort={onSort} align="left" />
                                <SortableTh label={t("watchlist.col_company")} sortKey="name" sort={sort} onSort={onSort} align="left" className="sticky left-[76px] z-20 bg-white" />
                                <th className="overline text-center px-2 py-2">{t("watchlist.col_mode")}</th>
                                <SortableTh label={t("watchlist.col_price")} sortKey="price" sort={sort} onSort={onSort} align="right" />
                                <SortableTh label={t("watchlist.col_mcap")} sortKey="mcap" sort={sort} onSort={onSort} align="right" />
                                <th className="overline text-right px-2 py-2">Próx. result.</th>
                                <SortableTh label={t("watchlist.col_rc")} sortKey="rc" sort={sort} onSort={onSort} align="right" />
                                <SortableTh label={t("watchlist.col_rv")} sortKey="rv" sort={sort} onSort={onSort} align="right" />
                                <SortableTh label={t("metrics.score")} sortKey="score" sort={sort} onSort={onSort} align="right" tip={QUAL_TIPS.scoreCompany} />
                                <SortableTh label={t("metrics.tam")} sortKey="tam" sort={sort} onSort={onSort} align="right" tip={QUAL_TIPS.tamCompany} />
                                <SortableTh label={t("metrics.kpi")} sortKey="kpi" sort={sort} onSort={onSort} align="right" />
                                <th className="overline text-center px-2 py-2">
                                    <MasterAlertToggle
                                        total={entries.length}
                                        activeCount={activeAlertCount}
                                        onChange={toggleAllAlerts}
                                        disabled={!user}
                                        info={alertInfoText(t, "watchlist")}
                                    />
                                </th>
                                <th className="px-2 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan="14" className="px-2 py-6 text-center font-mono text-[#4A4A4A]">{t("common.loading")}</td></tr>}
                            {sortedRows.map(({ entry, data: r }) => {
                                if (r.error) return (
                                    <tr key={r.ticker} className="border-b border-black/10">
                                        <td className="px-2 py-2 font-mono sticky left-0 z-10 bg-white">{r.ticker}</td>
                                        <td colSpan="12" className="px-2 py-2 text-[#B32A22] text-xs">{r.error}</td>
                                        <td className="px-2 py-2 text-right">
                                            <button onClick={() => handleRemove(r.ticker)} data-testid={`remove-${r.ticker}`}><Trash2 size={12} /></button>
                                        </td>
                                    </tr>
                                );
                                const cr = r.custom_ratios || {};
                                const isManual = entry.mode === "manual";
                                return (
                                    <tr key={r.ticker} className="group border-b border-black/10 hover:bg-[#F5E4D4]" data-testid={`watchlist-row-${r.ticker}`}>
                                        <td className="px-2 py-2 font-mono font-semibold sticky left-0 z-10 bg-white group-hover:bg-[#F5E4D4]">
                                            <Link to={`/company/${r.ticker}`} className="underline-offset-2 hover:underline">{r.ticker}</Link>
                                        </td>
                                        <td className="px-2 py-2 text-[#4A4A4A] max-w-[9rem] truncate" title={r.sector || "Sin clasificar"} data-testid={`sector-${r.ticker}`}>{r.sector || "Sin clasificar"}</td>
                                        <td className="px-2 py-2 text-[#4A4A4A] sticky left-[76px] z-10 bg-white group-hover:bg-[#F5E4D4]">{r.name}</td>
                                        <td className="px-2 py-2 text-center">
                                            {isManual ? (
                                                <span className="overline px-1.5 py-0.5 border border-[#1D7044] text-[#1D7044] bg-white text-[10px]" data-testid={`mode-${r.ticker}`}>MAN</span>
                                            ) : (
                                                <span className="overline px-1.5 py-0.5 border border-black/30 text-[#4A4A4A] bg-white text-[10px]" data-testid={`mode-${r.ticker}`}>AUTO</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">{fmtPrice(displayCur && displayCur !== "NATIVE" ? fxConvert(r.current_price, r.currency) : r.current_price, displayCur && displayCur !== "NATIVE" ? displayCur : r.currency)}</td>
                                        <td className="px-2 py-2 text-right font-mono">{fmtNum(displayCur && displayCur !== "NATIVE" ? fxConvert(r.market_cap, r.currency) : r.market_cap)}</td>
                                        <td className="px-2 py-2 text-right"><NextEarnings iso={r.next_earnings_date} testid={`wl-earnings-${r.ticker}`} /></td>
                                        <td className="px-2 py-2 text-right font-mono" style={{ color: ratioColor(cr.ratio_compra_pct) }} data-testid={`rc-${r.ticker}`}>
                                            {fmtPctSigned(cr.ratio_compra_pct)}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono" style={{ color: ratioColor(cr.ratio_venta_pct, "venta") }} data-testid={`rv-${r.ticker}`}>
                                            {fmtPctSigned(cr.ratio_venta_pct)}
                                        </td>
                                        {(() => { const q = qual[r.ticker]; return (<>
                                        <td className="px-2 py-2 text-right font-mono" data-testid={`score-${r.ticker}`}>{q?.score == null ? "—" : <span style={{ color: q.score >= 70 ? "#1D7044" : q.score >= 50 ? "#B8860B" : "#B32A22" }}>{Number(q.score).toFixed(1)}</span>}</td>
                                        <td className="px-2 py-2 text-right font-mono" data-testid={`tam-${r.ticker}`}>{q?.tam == null ? "—" : Number(q.tam).toFixed(2)}</td>
                                        <td className="px-2 py-2 text-right font-mono" data-testid={`kpi-${r.ticker}`}>{q?.kpi_coef == null ? "—" : <span style={{ color: q.kpi_coef > 1.05 ? "#1D7044" : q.kpi_coef < 0.95 ? "#B32A22" : "#B8860B", fontWeight: 600 }}>{Number(q.kpi_coef).toFixed(2)}</span>}</td>
                                        </>); })()}
                                        <td className="px-2 py-2 text-center">
                                            <AlertToggle
                                                enabled={!!entry.alert_enabled}
                                                onChange={(v) => { setWatchlistAlert(r.ticker, v); toast.success(v ? t("alerts.row_on") : t("alerts.row_off")); }}
                                                testid={r.ticker}
                                            />
                                        </td>
                                        <td className="px-2 py-2 text-right">
                                            <button onClick={() => handleRemove(r.ticker)} className="text-[#B32A22] hover:text-black" data-testid={`remove-${r.ticker}`}>
                                                <Trash2 size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" ref={donutRef}>
                    <PortfolioDonut items={mcapHoldings} currency={donutCur} testid="watchlist-donut-table" title="Capitalización por empresa" columns={2} linkTickers totalCompact showValue />
                    <PortfolioDonut items={mcapSectorHoldings} currency={donutCur} testid="watchlist-donut-table-sector" title="Capitalización por sector" totalCompact showValue />
                </div>
                </>
            )}
            {showAdd && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(17,17,17,0.5)" }} data-testid="watchlist-add-dialog" onClick={() => { setShowAdd(false); setAddInput(""); }}>
                    <div className="bg-white border border-black p-6 w-full max-w-md shadow-[6px_6px_0_0_rgba(17,17,17,0.15)]" onClick={(e) => e.stopPropagation()}>
                        <h2 className="font-serif text-2xl mb-1">Añadir a Nivel 2</h2>
                        <p className="text-sm text-[#4A4A4A] mb-4 leading-snug">Busca una acción por ticker o nombre y añádela a tu lista de seguimiento.</p>
                        <TickerAutocomplete
                            value={addInput}
                            onChange={setAddInput}
                            onPick={(r) => addTicker(r.symbol)}
                            onEnter={() => addTicker(addInput)}
                            placeholder="AAPL, SAN.MC, VOD.L…"
                            testid="watchlist-add-input"
                        />
                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => { setShowAdd(false); setAddInput(""); }} className="btn-ghost" data-testid="watchlist-add-cancel">Cancelar</button>
                            <button onClick={() => addTicker(addInput)} className="btn-primary inline-flex items-center gap-1" data-testid="watchlist-add-confirm"><Plus size={14} /> Añadir</button>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmDialog
                open={!!confirmDel}
                title="¿Quitar de Nivel 2?"
                message={`Se quitará ${confirmDel || ""} de tu lista de seguimiento y el borrado se sincronizará en todos tus dispositivos.`}
                confirmLabel="Quitar"
                onConfirm={doRemove}
                onCancel={() => setConfirmDel(null)}
                testid="watchlist-confirm-remove"
            />
        </div>
    );
}
